import type { BrandListItem } from '@/api/models';

/**
 * Presentational config + row shape rendered by the Brands table.
 *
 * The row shape is the bos-api's `BrandListItem` verbatim — the table is wired
 * to `useBrandListQuery` and surfaces every field it returns.
 */

export type Brand = BrandListItem;

export type BrandRowAction = 'edit' | 'delete';

/** Icon shown in the thumbnail cell when a brand has no image. */
export const NO_IMAGE_ICON = 'carbon:no-image';

/** Stable column ids — shared between the View columns and sort descriptor. */
export const BRAND_COLUMN = {
    image: 'image',
    name: 'name',
    actions: 'actions',
} as const;

export const BRAND_ROW_ACTIONS: {
    id: BrandRowAction;
    icon: string;
    labelKey: string;
    variant?: 'danger';
}[] = [
    {
        id: 'edit',
        icon: 'gravity-ui:pencil',
        labelKey: 'brands.components.brandTable.actions.edit',
    },
    {
        id: 'delete',
        icon: 'gravity-ui:trash-bin',
        labelKey: 'brands.components.brandTable.actions.delete',
        variant: 'danger',
    },
];

/** Default rows shown per page in the brands table (client-side pagination). */
export const BRANDS_PAGE_SIZE = 10;

/** Selectable page sizes for the brands table's rows-per-page control. */
export const BRANDS_PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200];

/**
 * Compute the page numbers to render in a pagination control, collapsing long
 * ranges with `'ellipsis'` markers (first/last always shown, plus a window
 * around the current page). Pure — unit-tested directly.
 */
export const getPaginationWindow = (
    totalPages: number,
    current: number,
): (number | 'ellipsis')[] => {
    const pages: (number | 'ellipsis')[] = [];
    if (totalPages <= 7) {
        for (let i = 1; i <= totalPages; i += 1) {
            pages.push(i);
        }
        return pages;
    }
    pages.push(1);
    if (current > 3) {
        pages.push('ellipsis');
    }
    const start = Math.max(2, current - 1);
    const end = Math.min(totalPages - 1, current + 1);
    for (let i = start; i <= end; i += 1) {
        pages.push(i);
    }
    if (current < totalPages - 2) {
        pages.push('ellipsis');
    }
    pages.push(totalPages);
    return pages;
};
